import {useCallback, useEffect, useRef, useState} from 'react';

const STORAGE_KEY = 'ticketing.aiSummaryCollapse.v1';

interface Persisted {
	seen: string[];
	expanded: string[]; // tickets the user explicitly re-expanded after first view
}

function read(): Persisted {
	if (typeof window === 'undefined') return {seen: [], expanded: []};
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) return {seen: [], expanded: []};
		const parsed = JSON.parse(raw) as Partial<Persisted>;
		return {
			seen: Array.isArray(parsed.seen) ? parsed.seen.filter((s): s is string => typeof s === 'string') : [],
			expanded: Array.isArray(parsed.expanded) ? parsed.expanded.filter((s): s is string => typeof s === 'string') : [],
		};
	} catch {
		return {seen: [], expanded: []};
	}
}

function write(state: Persisted) {
	if (typeof window === 'undefined') return;
	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
	} catch {
		// localStorage disabled / quota exceeded — ignore.
	}
}

export interface UseAiSummaryCollapse {
	collapsed: boolean;
	toggle: () => void;
}

/**
 * AC #6: collapsed-by-default after the first view, per user per ticket.
 * The display decision is frozen at mount: the FIRST view of a ticket is
 * expanded (and recorded as "seen" so the NEXT visit defaults to collapsed);
 * a ticket already seen mounts collapsed unless the user explicitly
 * re-expanded it. Marking "seen" is a persistence side effect only — it must
 * not collapse the block mid-view.
 */
export function useAiSummaryCollapse(ticketId: string): UseAiSummaryCollapse {
	// Snapshot the persisted state once, at mount, to decide the initial display
	// state. Recomputing after marking "seen" would collapse the first view.
	const initial = useRef<Persisted>(read());
	const seenAtMount = initial.current.seen.includes(ticketId);
	const expandedAtMount = initial.current.expanded.includes(ticketId);

	const [collapsed, setCollapsed] = useState<boolean>(seenAtMount && !expandedAtMount);

	// Mark the ticket seen on first mount (persist only; does not affect display).
	useEffect(() => {
		const current = read();
		if (!current.seen.includes(ticketId)) {
			write({...current, seen: [...current.seen, ticketId]});
		}
	}, [ticketId]);

	const toggle = useCallback(() => {
		setCollapsed(prev => {
			const next = !prev;
			// Persist the explicit re-expand override so it survives navigation.
			const current = read();
			const isOverridden = current.expanded.includes(ticketId);
			if (!next && !isOverridden) {
				write({...current, expanded: [...current.expanded, ticketId]});
			} else if (next && isOverridden) {
				write({...current, expanded: current.expanded.filter(id => id !== ticketId)});
			}
			return next;
		});
	}, [ticketId]);

	return {collapsed, toggle};
}
